import {
  camelCase,
  filter,
  isArray,
  isEmpty,
  map,
  reverse,
  size,
  trim,
  values,
} from 'lodash';
import { useEffect, useState } from 'react';
import config from '../../../common/config';
import {
  NOT_INCLUDED,
  NO_INFORMATION,
} from '../../../common/helpers/constants';
import { emitter, WINDOW_RESIZE } from '../../../common/helpers/ui/EventHelper';
import { useWindowSize } from '../../../common/helpers/utility/useWindowSize';
import projectResource from '../../../locales/th/project.json';
import ShowMore from '../../showMore/ShowMore';
import SectionTitle from '../sectionTitle/SectionTitle';
import {
  MainDetailAttribute,
  MainDetailAttributeData,
  MainDetailContent,
  MainDetailContentWrapper,
  MainDetailTitle,
  MainDetailWrapper,
} from './MainDetail.style';
import {
  getGeneralDetailsConfig,
  getPriceInformationConfig,
} from './mainDetailConfig';

interface InfoItem {
  label: string;
  value: string;
  hasLink?: string;
  link?: string;
}
interface Coordinate {
  lat?: number;
  lon?: number;
}

interface RoomType {
  status: string;
  name: string;
  min_size?: number;
  max_size?: number;
}
interface ProjectData {
  propertyType?: string[];
  buildingProfile?: object;
  location?: {
    coordinate?: Coordinate;
  };
  developer?: object;
  priceTypes?: object;
  roomTypes?: RoomType[];
}
interface MainDetailProps {
  projectData: ProjectData;
  window?: Window;
}
const MainDetail: React.FC<MainDetailProps> = ({ projectData }) => {
  const {
    propertyType = [],
    buildingProfile = {},
    location = {},
    developer = {},
    priceTypes = {},
    roomTypes: rooms = [],
  } = projectData;
  const { coordinate = {} } = location;
  const getGeneralDetails = getGeneralDetailsConfig(
    propertyType,
    buildingProfile,
    coordinate,
    developer,
  );
  const getPriceDetails = getPriceInformationConfig(priceTypes);

  const [generalDetails, setGeneralDetails] = useState([]);
  const [priceDetails, setPriceDetails] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [showGeneralDetails, setShowGeneralDetails] =
    useState(getGeneralDetails);
  const [showPriceDetails, setShowPriceDetails] = useState(getPriceDetails);
  const [showRoomTypes, setShowRoomTypes] = useState(rooms);
  const [isShowMoreGeneralDetails, setIsShowMoreGeneralDetails] =
    useState(false);
  const [isShowMorePriceDetails, setIsShowMorePriceDetails] = useState(false);
  const [isShowMoreRoomTypes, setIsShowMoreRoomTypes] = useState(false);
  const windowSize = useWindowSize();

  useEffect(() => {
    const handleShowInformationFields = () => {
      const generalDetailsWithValue = filter(
        generalDetails,
        (item) => !isEmpty(item.value),
      );
      setShowGeneralDetails(generalDetailsWithValue);

      const priceDetailsWithValue = filter(
        priceDetails,
        (item) => !isEmpty(trim(item.value)),
      );
      setShowPriceDetails(priceDetailsWithValue);

      const roomTypesWithValue = filter(
        roomTypes,
        (item) => item.status === 'Y',
      );
      setShowRoomTypes(roomTypesWithValue);
    };

    handleShowInformationFields();

    if (config.isBrowser) {
      emitter.on(WINDOW_RESIZE, handleShowInformationFields);
    }
    return () => {
      if (config.isBrowser) {
        emitter.off(WINDOW_RESIZE, handleShowInformationFields);
      }
    };
  }, [generalDetails, priceDetails, roomTypes, windowSize]);

  useEffect(() => {
    const {
      propertyType = [],
      buildingProfile = {},
      location = {},
      developer = {},
      priceTypes = {},
      roomTypes = [],
    } = projectData;
    const { coordinate = {} } = location;
    const getGeneralDetails = getGeneralDetailsConfig(
      propertyType,
      buildingProfile,
      coordinate,
      developer,
    );
    const getPriceDetails = getPriceInformationConfig(priceTypes);
    setGeneralDetails(getGeneralDetails);
    setPriceDetails(getPriceDetails);
    setRoomTypes(roomTypes);
  }, [projectData]);

  const setRoomTypeResult = (data: RoomType) => {
    const generalDetailSquareMeters =
      projectResource.generalDetail.squareMeters;
    let result = NO_INFORMATION;
    const unit =
      data.min_size && data.max_size ? ' ' + generalDetailSquareMeters : '';
    const separator = data.min_size && data.max_size ? ' - ' : '';
    if (data.status === 'Y') {
      result = data.min_size + separator + data.max_size + unit;
    }
    if (data.status === 'N') {
      result = NOT_INCLUDED;
    }
    return result;
  };

  const showGeneralDetailsMoreInfo = () => {
    setGeneralDetails(generalDetails);
    setIsShowMoreGeneralDetails(false);
  };

  const showPriceDetailsMoreInfo = () => {
    setPriceDetails(priceDetails);
    setIsShowMorePriceDetails(false);
  };

  const showRoomTypesMoreInfo = () => {
    setRoomTypes(roomTypes);
    setIsShowMoreRoomTypes(false);
  };

  const renderItem = (infoItem: InfoItem) => {
    if (isArray(infoItem.value)) {
      const separator = ' ,';
      return map(reverse(values(infoItem.value)), (value, i) => {
        return infoItem.hasLink ? (
          <span key={i}>
            <a href={'/' + value + '/'} data-test={'main-detail-link'}>
              {value}
            </a>
            {size(infoItem.value) !== i + 1 ? separator : ''}
          </span>
        ) : (
          value
        );
      });
    }
    return infoItem.hasLink ? (
      <a href={infoItem.link} data-test={'main-detail-link'}>
        {infoItem.value}
      </a>
    ) : (
      infoItem.value
    );
  };

  const showMoreText = projectResource.showMore;
  const generalDetailTitle = projectResource.generalDetail.title;
  const priceInformationTitle = projectResource.priceInformation.title;
  const roomTypeTitle = projectResource.roomType.title;

  return (
    <MainDetailWrapper>
      {!isEmpty(showGeneralDetails) && (
        <MainDetailContentWrapper className="general-detail">
          <SectionTitle>{generalDetailTitle}</SectionTitle>
          <MainDetailContent>
            {map(
              showGeneralDetails,
              (generalDetail: InfoItem, index: number) => {
                return (
                  !isEmpty(generalDetail.value) && (
                    <MainDetailAttribute key={index}>
                      <MainDetailTitle data-test={'general-detail-title'}>
                        {generalDetail.label}
                      </MainDetailTitle>
                      <MainDetailAttributeData
                        data-test={'general-detail-attribute-data'}
                      >
                        {renderItem(generalDetail)}
                      </MainDetailAttributeData>
                    </MainDetailAttribute>
                  )
                );
              },
            )}
          </MainDetailContent>
          {isShowMoreGeneralDetails && (
            <ShowMore
              isUsedInProjectPage={true}
              showMoreText={showMoreText}
              className="detail-show-more"
              showMoreInfo={() => showGeneralDetailsMoreInfo()}
            />
          )}
        </MainDetailContentWrapper>
      )}

      {!isEmpty(showPriceDetails) && (
        <MainDetailContentWrapper className="price-detail">
          <SectionTitle>{priceInformationTitle}</SectionTitle>
          <MainDetailContent>
            {map(showPriceDetails, (priceDetail, index) => {
              return (
                trim(priceDetail.value) && (
                  <MainDetailAttribute key={index}>
                    <MainDetailTitle data-test={'price-detail-title'}>
                      {priceDetail.label}
                    </MainDetailTitle>
                    <MainDetailAttributeData
                      data-test={'price-detail-attribute-data'}
                    >
                      {priceDetail.value}
                    </MainDetailAttributeData>
                  </MainDetailAttribute>
                )
              );
            })}
          </MainDetailContent>
          {isShowMorePriceDetails && (
            <ShowMore
              isUsedInProjectPage={true}
              showMoreText={showMoreText}
              className="detail-show-more"
              showMoreInfo={() => showPriceDetailsMoreInfo()}
            />
          )}
        </MainDetailContentWrapper>
      )}

      {!isEmpty(showRoomTypes) && (
        <MainDetailContentWrapper className="room-types">
          <SectionTitle>{roomTypeTitle}</SectionTitle>
          <MainDetailContent>
            {map(showRoomTypes, (item, i) => {
              return (
                item.status === 'Y' && (
                  <MainDetailAttribute key={i}>
                    <MainDetailTitle data-test={'room-type-detail-title'}>
                      {
                        projectResource.roomType[
                          camelCase(
                            item.name,
                          ) as keyof typeof projectResource.roomType
                        ]
                      }
                    </MainDetailTitle>
                    <MainDetailAttributeData
                      data-test={'room-type-detail-attribute-data'}
                    >
                      {setRoomTypeResult(item)}
                    </MainDetailAttributeData>
                  </MainDetailAttribute>
                )
              );
            })}
          </MainDetailContent>
          {isShowMoreRoomTypes && (
            <ShowMore
              isUsedInProjectPage={true}
              showMoreText={showMoreText}
              className="detail-show-more"
              showMoreInfo={() => showRoomTypesMoreInfo()}
            />
          )}
        </MainDetailContentWrapper>
      )}
    </MainDetailWrapper>
  );
};

export { MainDetail };
